/**
 * The single inline callout / banner primitive for every app (BL-1521,
 * ratifying the `Alert` row of the ADR 030 Canon).
 *
 * The merge collapses three implementations of one job: admin-web's `Alert`
 * (the settings-v2 `ui-alert` callout, 60 importers), bidder-web's `Alert`
 * (BL-716, Tailwind `--bw-*` tones, 21 importers), and 53 hand-rolled
 * `bg-red-50 border border-red-200` error boxes across the admin dashboard —
 * 4 radii, 6 paddings, 4 text sizes, 2 reds, and 47 of them silent to screen
 * readers. All three drew the same shape: a toned box, an optional leading
 * icon, an optional bold title over a body, and (bidder only) a dismiss ×.
 * They differed only in VALUES.
 *
 * WHY a `dialect` object rather than a pile of `className` props: ADR 030's
 * foundation is shared but its dialects are deliberately different, and
 * `sharing.md` § 1 caps a per-app skin at a thin wrapper. One frozen object of
 * class names per app keeps the appearance a piece of DATA the skin declares
 * once. Three dialects bind to this core: admin settings-v2 (`ui-alert`, only
 * styled where settings-v2.css is loaded), admin classic (Tailwind — the
 * Design-approved treatment every migrated error banner uses, and the one
 * that works on every route), and bidder.
 *
 * THE ONE DELIBERATE BEHAVIOUR CHANGE in this merge: a `danger` banner is
 * exposed to assistive tech as an alert UNCONDITIONALLY — `role="alert"` +
 * `aria-live="assertive"`, and a caller-supplied `role` is ignored for that
 * tone. Both pre-merge twins made the role opt-in, which is exactly how 49 of
 * 55 admin error banners came to be silent: a screen-reader operator clicked
 * Save, heard nothing, and the only signal the action failed was a colour they
 * could not see. Putting the rule here, once, is the point of the primitive;
 * a skin that re-declared it (guarded by `Alert.skins.guard.test.ts`) would
 * re-open the door. The other three tones keep the caller's `role` — a static
 * info callout announced assertively on every mount is the opposite defect.
 *
 * WHY `rootStyle` exists: admin's v2 dialect carries its bottom margin as an
 * inline style, which no class can beat — admin composes with `clsx`, which
 * concatenates rather than resolves conflicting utilities (ADR 030 § 3b;
 * `twMerge` is the open BL-1004). The slot lets that dialect keep its margin
 * exactly where it was without the core knowing why.
 */
'use client';

import clsx from 'clsx';
import { X } from 'lucide-react';
import type { AriaRole, CSSProperties, ElementType, ReactNode } from 'react';

/** Semantic tone. `danger` is the only tone with behaviour attached (see the header). */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

/**
 * Per-app appearance binding — class names only, no structure.
 *
 * Every slot except `root` and `tones` is optional: a consumer with no dialect
 * gets the bare accessible structure, which is the right default for a fourth
 * app that hasn't declared one yet.
 */
export interface AlertDialect {
    /** The banner box — layout, padding, radius, base type treatment. */
    root: string;
    /** Fill / border / text per tone. */
    tones: Record<AlertTone, string>;
    /**
     * Optional wrapper around the icon slot.
     *
     * OMIT IT to render the caller's icon element bare as the first child
     * (admin's clerk-console callers pass a fully-classed icon and expect no
     * extra wrapper). Provide it for a dialect that positions the icon itself.
     */
    icon?: string;
    /**
     * Wrapper around the body when there is NO title.
     *
     * OMIT IT to render the children bare as direct children of the root
     * (admin's v2 dialect — its 60 callers were written against that DOM, and a
     * wrapper would re-flow the few that pass sibling nodes). The two Tailwind
     * dialects provide `min-w-0 flex-1` so long copy wraps beside the icon.
     */
    body?: string;
    /** Wrapper around title + body when a title IS set (`space-y-1`, `min-w-0 flex-1`). */
    stack?: string;
    /** The bold first line rendered above the body when `title` is set. */
    title?: string;
    /**
     * Element tags for the title line and the body line under a title.
     * Dialect-controlled because the pre-merge twins disagreed (`<p>` in
     * admin's v2, `<div>` in bidder); normalising them would change the DOM
     * 60 callers and their tests were written against. Default `div`.
     */
    titleAs?: ElementType;
    bodyAs?: ElementType;
    /** The dismiss × button, rendered only when `onDismiss` is passed. */
    dismiss?: string;
    /** Inline style on the root — see the header for why a dialect needs one. */
    rootStyle?: CSSProperties;
}

/** Props for the shared Alert core. Skins re-export their own app-facing shape. */
export interface AlertProps {
    /** Semantic tone. Defaults to `info`. */
    tone?: AlertTone;
    /**
     * ARIA role for the non-danger tones — `status` (polite live region),
     * `note`, or none. Ignored for `danger`, which is always `alert`.
     */
    role?: AriaRole;
    /** Optional leading icon, rendered verbatim (a lucide element or inline SVG). */
    icon?: ReactNode;
    /** Optional bold first line above the body. */
    title?: ReactNode;
    /** Banner body. */
    children?: ReactNode;
    /** When provided, renders a × button that calls this. */
    onDismiss?: () => void;
    /** Extra classes on the root — the caller's spacing lives here. */
    className?: string;
    /** Emitted as `data-testid` on the root. */
    testId?: string;
    /** The app's appearance binding. */
    dialect?: AlertDialect;
    /** Inline style on the root, merged over `dialect.rootStyle`. */
    style?: CSSProperties;
}

/** Resolve the live-region role: danger is always an alert; other tones take the caller's. */
function resolveRole(tone: AlertTone, role: AriaRole | undefined): AriaRole | undefined {
    return tone === 'danger' ? 'alert' : role;
}

/**
 * Inline banner in four semantic tones — the one callout shape for every app.
 *
 * @param props - {@link AlertProps}
 * @returns The rendered banner.
 */
export function Alert({
    tone = 'info',
    role,
    icon,
    title,
    children,
    onDismiss,
    className,
    testId,
    dialect,
    style,
}: AlertProps) {
    const resolvedRole = resolveRole(tone, role);
    const ariaLive = resolvedRole === 'alert' ? 'assertive' : resolvedRole === 'status' ? 'polite' : undefined;
    const rootStyle = dialect?.rootStyle || style ? { ...dialect?.rootStyle, ...style } : undefined;

    // A title needs the stacked layout the root's own flex row can't provide;
    // a bare message stays a direct child unless the dialect asks for a wrapper,
    // so the long-standing single-line callouts render exactly as they always have.
    const TitleTag: ElementType = dialect?.titleAs ?? 'div';
    const BodyTag: ElementType = dialect?.bodyAs ?? 'div';
    const body =
        title != null ? (
            <div className={dialect?.stack}>
                <TitleTag className={dialect?.title}>{title}</TitleTag>
                {children != null ? <BodyTag>{children}</BodyTag> : null}
            </div>
        ) : dialect?.body != null && children != null ? (
            <div className={dialect.body}>{children}</div>
        ) : (
            children
        );

    return (
        <div
            role={resolvedRole}
            aria-live={ariaLive}
            data-testid={testId}
            className={clsx(dialect?.root, dialect?.tones[tone], className)}
            style={rootStyle}
        >
            {icon != null ? dialect?.icon ? <span className={dialect.icon}>{icon}</span> : icon : null}
            {body}
            {onDismiss ? (
                <button type="button" aria-label="Dismiss" onClick={onDismiss} className={dialect?.dismiss}>
                    <X className="h-4 w-4" aria-hidden />
                </button>
            ) : null}
        </div>
    );
}
